// Storage backends of the device-local speech spool (C-7 SpeechSpool).
//
// A spool session stores its frames in chunks of CHUNK_FRAMES frames aligned
// to seq multiples (frames 0–9, 10–19, …), so a meeting's acknowledged
// 100-frame range [100k, 100k+99] always deletes whole chunks. The IndexedDB
// backend (./idb.ts) is the durable one; this memory backend is the fallback
// when IndexedDB is unavailable (private mode, blocked, non-browser tests).
import type { SpoolMeta } from '../contracts';

/** Frames per stored chunk (one IndexedDB write per second of audio). */
export const CHUNK_FRAMES = 10;

export type StoredFrame = { seq: number; sampleOffset: number; samples: number; pcm: ArrayBuffer };
export type StoredChunk = { sessionId: string; firstSeq: number; frames: StoredFrame[] };

export const chunkStart = (seq: number) => Math.floor(seq / CHUNK_FRAMES) * CHUNK_FRAMES;

export interface SpoolBackend {
  /** Supplied PCM rows and session metadata commit together. */
  putSession(meta: SpoolMeta, chunks?: StoredChunk[]): Promise<void>;
  getSession(id: string): Promise<SpoolMeta | null>;
  /** Every session, or one owner's. */
  listSessions(owner?: string): Promise<SpoolMeta[]>;
  /** Deletes the session and all its chunks. */
  deleteSession(id: string): Promise<void>;
  putChunk(chunk: StoredChunk): Promise<void>;
  deleteChunk(sessionId: string, firstSeq: number): Promise<void>;
  /** Chunks with firstSeq in [fromFirstSeq, toFirstSeq], ascending, at most `limit` (all when omitted). */
  readChunks(
    sessionId: string,
    fromFirstSeq: number,
    toFirstSeq: number,
    limit?: number,
  ): Promise<StoredChunk[]>;
}

export function createMemorySpoolBackend(): SpoolBackend {
  const sessions = new Map<string, SpoolMeta>();
  const chunks = new Map<string, Map<number, StoredChunk>>();
  const chunksOf = (id: string) => {
    let map = chunks.get(id);
    if (!map) chunks.set(id, (map = new Map()));
    return map;
  };
  return {
    async putSession(meta, pairedChunks = []) {
      const paired = pairedChunks.map((chunk) => ({ ...chunk, frames: [...chunk.frames] }));
      for (const chunk of paired) chunksOf(chunk.sessionId).set(chunk.firstSeq, chunk);
      sessions.set(meta.id, { ...meta });
    },
    async getSession(id) {
      const meta = sessions.get(id);
      return meta ? { ...meta } : null;
    },
    async listSessions(owner) {
      return [...sessions.values()]
        .filter((meta) => owner === undefined || meta.owner === owner)
        .map((meta) => ({ ...meta }));
    },
    async deleteSession(id) {
      sessions.delete(id);
      chunks.delete(id);
    },
    async putChunk(chunk) {
      chunksOf(chunk.sessionId).set(chunk.firstSeq, { ...chunk, frames: [...chunk.frames] });
    },
    async deleteChunk(sessionId, firstSeq) {
      chunks.get(sessionId)?.delete(firstSeq);
    },
    async readChunks(sessionId, fromFirstSeq, toFirstSeq, limit) {
      return [...(chunks.get(sessionId)?.values() ?? [])]
        .filter((chunk) => chunk.firstSeq >= fromFirstSeq && chunk.firstSeq <= toFirstSeq)
        .sort((a, b) => a.firstSeq - b.firstSeq)
        .slice(0, limit ?? Number.POSITIVE_INFINITY)
        .map((chunk) => ({ ...chunk, frames: [...chunk.frames] }));
    },
  };
}
